// ChatBot.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  IconButton,
  Badge,
  Collapsible,
  Spinner,
} from "@chakra-ui/react";
import {
  FiMessageCircle,
  FiX,
  FiSend,
  FiUser,
  FiTool,
  FiDollarSign,
  FiCopy,
  FiCheck,
} from "react-icons/fi";

import { RiBarChartBoxAiLine } from "react-icons/ri";
import AvatarRoot from "@/components/Dashboard/Avatar";
import MarkdownRenderer from "@/components/Dashboard/MarkdownRenderer";

// 메시지 타입 정의
interface Message {
  id: string;
  type: "user" | "bot";
  content: string;
  timestamp: Date;
  data?: any;
  isStreaming?: boolean;
}

// 대시보드 데이터 타입
interface DashboardData {
  equipment: any[];
  alerts: any[];
  overallStatus: string;
  totalEquipment: number;
  onlineEquipment: number;
  warningEquipment: number;
  offlineEquipment: number;
}

interface ChatBotProps {
  dashboardData: DashboardData;
}

// 고유 ID 생성기 클래스
class UniqueIdGenerator {
  private counter = 0;
  private lastTimestamp = 0;

  generate(prefix: string = "msg"): string {
    const now = Date.now();

    if (now === this.lastTimestamp) {
      this.counter++;
    } else {
      this.counter = 0;
      this.lastTimestamp = now;
    }

    return `${prefix}-${now}-${this.counter}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// 복사 버튼 컴포넌트
const CopyButton: React.FC<{ content: string }> = ({ content }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      // 마크다운 형식을 일반 텍스트로 변환
      const plainText = content
        .replace(/#{1,6}\s/g, "") // 헤딩 제거
        .replace(/\*\*(.*?)\*\*/g, "$1") // bold 제거
        .replace(/\*(.*?)\*/g, "$1") // italic 제거
        .replace(/`(.*?)`/g, "$1") // 인라인 코드 제거
        .replace(/```[\s\S]*?```/g, "") // 코드 블록 제거
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // 링크 제거
        .replace(/^\s*[-*+]\s/gm, "• ") // 리스트 마커 변경
        .replace(/^\s*\d+\.\s/gm, "") // 숫자 리스트 마커 제거
        .trim();

      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("복사 실패:", error);
    }
  };

  return (
    <IconButton
      aria-label="copy message"
      size="md"
      variant="ghost"
      color="gray.500"
      onClick={handleCopy}
      opacity={0.7}
      _hover={{ opacity: 1, bg: "gray.100" }}
    >
      {copied ? <FiCheck color="green" /> : <FiCopy />}
    </IconButton>
  );
};

const ChatBot: React.FC<ChatBotProps> = ({ dashboardData }) => {
  // 고유 ID 생성기 인스턴스
  const idGenerator = useRef(new UniqueIdGenerator());

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome-msg-initial",
      type: "bot",
      content: `# 안녕하세요! 

**스마트 팩토리 정비 관리 AI 어시스턴트**입니다.

다음과 같은 도움을 드릴 수 있습니다:

## 주요 기능
- **정비 기록 분석** - 전체 정비 이력 종합 분석
- **비용 분석** - 정비 비용 현황 및 트렌드 분석  
- **장비별 현황** - 특정 장비의 정비 이력 조회
- **담당자별 현황** - 기술자별 작업 현황 분석

## 예시 질문

\`\`\`
정비 기록 전체 분석해줘
CNC 밀링머신 정비 이력은?
비용이 가장 많이 든 정비는?
서동욱 담당자 작업 현황은?
\`\`\`

> **팁**: 구체적인 장비명이나 담당자명을 언급하면 더 정확한 분석을 받을 수 있습니다!

---

### 정비 코드 안내
| 코드 | 의미 | 설명 |
|------|------|------|
| **PM** | 예방 정비 | Preventive Maintenance |
| **CM** | 교정 정비 | Corrective Maintenance |
| **PV** | 예측 정비 | Predictive Maintenance |
| **UP** | 업그레이드 | System Upgrade |`,
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showQuickQuestions, setShowQuickQuestions] = useState(false);
  const [maintenanceRecords, setMaintenanceRecords] = useState<any[]>([]);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 나머지 함수들은 이전과 동일...
  const generateMessageId = useCallback((prefix: string = "msg") => {
    return idGenerator.current.generate(prefix);
  }, []);

  useEffect(() => {
    const loadMaintenanceRecords = () => {
      try {
        const stored = localStorage.getItem("maintenanceRecords");
        if (stored) {
          setMaintenanceRecords(JSON.parse(stored));
        }
      } catch (error) {
        console.error("정비 기록 로드 실패:", error);
      }
    };

    loadMaintenanceRecords();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // 스트리밍 응답 처리 함수들은 이전과 동일...
  const handleStreamingResponse = async (
    endpoint: string,
    requestBody: any
  ) => {
    if (isLoading) {
      console.warn("이미 요청이 진행 중입니다.");
      return;
    }

    const streamingMessageId = generateMessageId("streaming");
    setStreamingMessageId(streamingMessageId);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    const botMessage: Message = {
      id: streamingMessageId,
      type: "bot",
      content: "",
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => {
      const exists = prev.some((msg) => msg.id === streamingMessageId);
      if (exists) {
        console.warn("중복 메시지 생성 방지:", streamingMessageId);
        return prev;
      }
      return [...prev, botMessage];
    });

    setIsLoading(true);

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("스트림 리더를 생성할 수 없습니다");
      }

      const decoder = new TextDecoder();
      let accumulatedContent = "";
      let buffer = "";

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.trim() === "") continue;

            if (line.startsWith("data: ")) {
              try {
                const jsonStr = line.slice(6).trim();
                if (jsonStr === "") continue;

                const data = JSON.parse(jsonStr);

                if (data.type === "chunk" && data.content) {
                  accumulatedContent += data.content;

                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId
                        ? {
                            ...msg,
                            content: accumulatedContent,
                            isStreaming: true,
                          }
                        : msg
                    )
                  );
                } else if (data.type === "done") {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId
                        ? { ...msg, isStreaming: false }
                        : msg
                    )
                  );
                  return;
                } else if (data.type === "error") {
                  throw new Error(
                    data.content || "서버에서 오류가 발생했습니다"
                  );
                }
              } catch (parseError) {
                console.error("JSON 파싱 오류:", parseError);
                if (line.includes("AI 응답 생성 중 오류 발생")) {
                  throw new Error("AI 서비스에서 오류가 발생했습니다.");
                }
                continue;
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error: any) {
      console.error("스트리밍 오류:", error);

      let errorMessage = "❌ **응답 생성 중 오류가 발생했습니다.**";

      if (error.name === "AbortError") {
        errorMessage = "⏹️ **응답 생성이 중단되었습니다.**";
      } else if (error.message?.includes("AI 응답 생성 중 오류")) {
        errorMessage =
          "❌ **AI 서비스가 일시적으로 이용 불가합니다.**\n\n잠시 후 다시 시도해주세요.";
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMessageId
            ? {
                ...msg,
                content: `${errorMessage}\n\n> 💡 다른 질문을 시도해보거나 잠시 후 다시 시도해주세요.`,
                isStreaming: false,
              }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
      setStreamingMessageId(null);
      abortControllerRef.current = null;
    }
  };

  // 나머지 함수들과 렌더링 부분은 이전과 동일하지만 마크다운 렌더링 적용
  const handleStopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setIsLoading(false);
    if (streamingMessageId) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMessageId
            ? {
                ...msg,
                isStreaming: false,
                content: msg.content + "\n\n⏹️ **응답이 중단되었습니다.**",
              }
            : msg
        )
      );
      setStreamingMessageId(null);
    }
  };

  const generateBotResponse = async (userMessage: string): Promise<void> => {
    await handleStreamingResponse(
      `${import.meta.env.VITE_API_URL}/api/v1/chats/pm/stream`,
      {
        message: userMessage,
        maintenance_records: maintenanceRecords,
        conversation_history: messages.slice(-10).map((msg) => ({
          id: msg.id,
          type: msg.type,
          content: msg.content,
          timestamp: msg.timestamp,
        })),
      }
    );
  };

  const analyzeMaintenanceRecords = async () => {
    if (isLoading) return;

    const userMessage: Message = {
      id: generateMessageId("user"),
      type: "user",
      content: "정비 기록 전체 분석해줘",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    await handleStreamingResponse(
      `${import.meta.env.VITE_API_URL}/api/v1/chats/pm/analyze/stream`,
      maintenanceRecords
    );
  };

  const analyzeCosts = async () => {
    if (isLoading) return;

    const userMessage: Message = {
      id: generateMessageId("user"),
      type: "user",
      content: "비용 분석해줘",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    await handleStreamingResponse(
      `${import.meta.env.VITE_API_URL}/api/v1/chats/pm/cost-analysis/stream`,
      maintenanceRecords
    );
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: generateMessageId("user"),
      type: "user",
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageContent = inputValue.trim();
    setInputValue("");

    const lower = messageContent.toLowerCase();
    if (lower.includes("전체 분석") || lower.includes("종합 분석")) {
      await handleStreamingResponse(
        `${import.meta.env.VITE_API_URL}/api/v1/chats/pm/analyze/stream`,
        maintenanceRecords
      );
      return;
    }

    if (lower.includes("비용 분석") || lower.includes("cost analysis")) {
      await handleStreamingResponse(
        `${import.meta.env.VITE_API_URL}/api/v1/chats/pm/cost-analysis/stream`,
        maintenanceRecords
      );
      return;
    }

    await generateBotResponse(messageContent);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickQuestions = [
    "정비 기록 전체 분석해줘",
    "비용 분석해줘",
    "CNC 밀링머신 정비 이력",
    "담당자별 작업 현황",
    "장비 상태 알려줘",
    "예정된 정비 일정은?",
  ];

  const handleQuickQuestion = async (question: string) => {
    if (isLoading) return;

    const userMessage: Message = {
      id: generateMessageId("user"),
      type: "user",
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setShowQuickQuestions(false);

    await generateBotResponse(question);
  };

  return (
    <Box position="fixed" bottom={4} right={4} zIndex={1000}>
      <Collapsible.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
      >
        <Collapsible.Content>
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            shadow="xl"
            w="680px" // 마크다운을 위해 더 넓게
            h={isMinimized ? "60px" : "800px"} // 더 높게
            mb={2}
            overflow="hidden"
            transition="all 0.3s"
          >
            {/* 헤더 */}
            <HStack
              bg="blue.500"
              color="white"
              p={3}
              justify="space-between"
              cursor={isMinimized ? "pointer" : "default"}
              onClick={isMinimized ? () => setIsMinimized(false) : undefined}
            >
              <HStack>
                <AvatarRoot icon={<FiTool />} bg="blue.600" />
                <VStack gap={0} align="start">
                  <Text fontSize="lg" fontWeight="bold">
                    정비 관리 AI ✨
                  </Text>
                  <HStack gap={1}>
                    <Badge
                      size="md"
                      variant="solid"
                      bg={isLoading ? "orange.500" : "green.500"}
                      color="white"
                    >
                      {isLoading ? "처리중..." : "온라인"}
                    </Badge>
                    {maintenanceRecords.length > 0 && (
                      <Badge
                        size="md"
                        variant="solid"
                        bg="purple.500"
                        color="white"
                      >
                        {maintenanceRecords.length}건 기록
                      </Badge>
                    )}
                    {/* <Badge size="md" variant="solid" bg="purple.500" color="white">
                      MD ✨
                    </Badge> */}
                  </HStack>
                </VStack>
              </HStack>

              <HStack>
                {isLoading && (
                  <IconButton
                    aria-label="stop streaming"
                    size="md"
                    variant="ghost"
                    color="white"
                    onClick={handleStopStreaming}
                  >
                    <FiX />
                  </IconButton>
                )}

                <IconButton
                  aria-label="toggle quick questions"
                  size="md"
                  variant="ghost"
                  color="white"
                  onClick={() => setShowQuickQuestions(!showQuickQuestions)}
                  title="빠른 질문"
                  _hover={{
                    bg: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                  }}
                  _active={{
                    bg: "rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <FiMessageCircle />
                </IconButton>

                <IconButton
                  aria-label="analyze all"
                  size="md"
                  variant="ghost"
                  color="white"
                  onClick={analyzeMaintenanceRecords}
                  disabled={isLoading}
                  title="전체 분석"
                  _hover={{
                    bg: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                  }}
                  _active={{
                    bg: "rgba(255, 255, 255, 0.2)",
                  }}
                  _disabled={{
                    opacity: 0.4,
                    cursor: "not-allowed",
                  }}
                >
                  <RiBarChartBoxAiLine />
                </IconButton>

                <IconButton
                  aria-label="cost analysis"
                  size="md"
                  variant="ghost"
                  color="white"
                  onClick={analyzeCosts}
                  disabled={isLoading}
                  title="비용 분석"
                  _hover={{
                    bg: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                  }}
                  _active={{
                    bg: "rgba(255, 255, 255, 0.2)",
                  }}
                  _disabled={{
                    opacity: 0.4,
                    cursor: "not-allowed",
                  }}
                >
                  <FiDollarSign />
                </IconButton>

                <IconButton
                  aria-label="close"
                  size="md"
                  variant="ghost"
                  color="white"
                  _hover={{
                    bg: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                  }}
                  onClick={() => setIsOpen(false)}
                >
                  <FiX />
                </IconButton>
              </HStack>
            </HStack>

            {!isMinimized && (
              <>
                {/* 메시지 영역 - 높이 조정 */}
                <VStack
                  gap={0}
                  align="stretch"
                  h={showQuickQuestions ? "540px" : "660px"} // 빠른 질문이 보일 때 높이 더 줄이기
                  overflowY="auto"
                  p={3}
                  bg="gray.50"
                >
                  {messages.map((message) => (
                    <HStack
                      key={message.id}
                      align="start"
                      justify={
                        message.type === "user" ? "flex-end" : "flex-start"
                      }
                      mb={4}
                    >
                      {message.type === "bot" && (
                        <AvatarRoot icon={<FiTool />} bg="blue.500" />
                      )}

                      <Box
                        maxW="88%"
                        bg={message.type === "user" ? "blue.500" : "white"}
                        color={message.type === "user" ? "white" : "gray.800"}
                        px={5}
                        py={4}
                        borderRadius="lg"
                        border={message.type === "bot" ? "1px solid" : "none"}
                        borderColor="gray.200"
                        shadow={message.type === "bot" ? "sm" : "none"}
                        position="relative"
                        css={
                          message.type === "bot"
                            ? {
                                "& table": {
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  margin: "16px 0",
                                  fontSize: "14px",
                                },
                                "& th, & td": {
                                  border: "1px solid #e2e8f0",
                                  padding: "8px 12px",
                                  textAlign: "left",
                                  verticalAlign: "top",
                                },
                                "& th": {
                                  backgroundColor: "#f8fafc",
                                  fontWeight: "600",
                                  color: "#2d3748",
                                },
                                "& tbody tr:nth-of-type(even)": {
                                  backgroundColor: "#f9f9f9",
                                },
                                "& tbody tr:hover": {
                                  backgroundColor: "#f1f5f9",
                                },
                                "& code": {
                                  backgroundColor: "#f7fafc",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                },
                                "& pre": {
                                  backgroundColor: "#f7fafc",
                                  padding: "12px",
                                  borderRadius: "8px",
                                  overflow: "auto",
                                  fontSize: "13px",
                                },
                              }
                            : {}
                        }
                      >
                        {/* 마크다운 렌더링 */}
                        {message.type === "bot" ? (
                          <MarkdownRenderer
                            content={message.content}
                            fontSize="md"
                          />
                        ) : (
                          <Text fontSize="md" whiteSpace="pre-line">
                            {message.content}
                          </Text>
                        )}

                        {/* 스트리밍 인디케이터 */}
                        {message.isStreaming && (
                          <HStack mt={3} justify="space-between">
                            <HStack>
                              <Spinner size="sm" color="blue.500" />
                              <Text fontSize="sm" color="gray.500">
                                응답 생성 중...
                              </Text>
                            </HStack>
                            <Button
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={handleStopStreaming}
                            >
                              중단
                            </Button>
                          </HStack>
                        )}

                        {/* 메시지 하단 정보 */}
                        <HStack
                          justify="space-between"
                          mt={message.type === "bot" ? 3 : 2}
                          opacity={0.7}
                        >
                          <Text
                            fontSize="xs"
                            textAlign={
                              message.type === "user" ? "right" : "left"
                            }
                          >
                            {message.timestamp.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Text>

                          {/* 복사 버튼 (봇 메시지에만) */}
                          {message.type === "bot" && !message.isStreaming && (
                            <CopyButton content={message.content} />
                          )}
                        </HStack>
                      </Box>

                      {message.type === "user" && (
                        <AvatarRoot icon={<FiUser />} bg="gray.500" />
                      )}
                    </HStack>
                  ))}

                  <div ref={messagesEndRef} />
                </VStack>

                {/* 빠른 질문 영역 - 단순하게 모든 질문 표시 */}
                {showQuickQuestions && (
                  <Box
                    p={3}
                    bg="white"
                    borderTop="1px solid"
                    borderColor="gray.100"
                    maxH="120px" // 높이 제한
                    overflowY="auto" // 스크롤 가능
                  >
                    <HStack justify="space-between" mb={2}>
                      <Text
                        fontSize="md"
                        color="gray.500"
                        fontWeight="semibold"
                      >
                        💡 빠른 질문
                      </Text>
                      <Badge size="md" variant="outline" colorScheme="blue">
                        {quickQuestions.length}
                      </Badge>
                    </HStack>

                    <VStack gap={1} align="stretch">
                      {quickQuestions.map((question, index) => (
                        <Button
                          key={`quick-question-${index}-${question.slice(0, 10)}`}
                          size="md"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => handleQuickQuestion(question)}
                          disabled={isLoading}
                          textAlign="left"
                          justifyContent="flex-start"
                          _hover={{ bg: "blue.50" }}
                          fontSize="md"
                        >
                          {question}
                        </Button>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* 입력 영역 - 항상 맨 아래 고정 */}
                <HStack
                  p={3}
                  bg="white"
                  borderTop="1px solid"
                  borderColor="gray.200"
                >
                  <Input
                    ref={inputRef}
                    placeholder="메시지를 입력하세요... (Shift+Enter로 줄바꿈)"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    fontSize="xl"
                    height="40px"
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px blue.500",
                    }}
                  />
                  <IconButton
                    aria-label="send"
                    colorScheme="blue"
                    size="md"
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading}
                  >
                    {isLoading ? <Spinner size="md" /> : <FiSend />}
                  </IconButton>
                </HStack>
              </>
            )}
          </Box>
        </Collapsible.Content>

        {/* 토글 버튼 */}
        <Collapsible.Trigger asChild>
          <IconButton
            aria-label="toggle chat"
            colorScheme="blue"
            size="2xl"
            borderRadius="full"
            shadow="lg"
            position="relative"
          >
            {isOpen ? <FiX /> : <FiMessageCircle />}

            {!isOpen && dashboardData.warningEquipment > 0 && (
              <Badge
                position="absolute"
                top="-1"
                right="-1"
                bg="red.500"
                color="white"
                borderRadius="full"
                minW="20px"
                h="20px"
                fontSize="md"
              >
                {dashboardData.warningEquipment}
              </Badge>
            )}

            {isLoading && !isOpen && (
              <Badge
                position="absolute"
                top="-1"
                left="-1"
                bg="blue.500"
                color="white"
                borderRadius="full"
                minW="20px"
                h="20px"
                fontSize="md"
              >
                <Spinner size="md" />
              </Badge>
            )}
          </IconButton>
        </Collapsible.Trigger>
      </Collapsible.Root>
    </Box>
  );
};

export default ChatBot;
